import React, {useCallback, useEffect, useMemo} from 'react';
import {useDispatch} from 'react-redux';

import {ErrorBoundary} from '@sentry/react';
import {clearSelection} from 'src/components/data-table-selection';
import {ActionWithReasonButton} from 'src/components/save-with-reason/action-with-reason-button';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {useDataTableColumnsWidthOnResize} from 'src/hooks/useDataTableColumnsWidthOnResize';
import {NoResultPage, PreparedEntitlementsView, ResourceSuggest, useExpandRoles} from 'src/modules/entitlements';
import {ErrorPage} from 'src/modules/error-page/error-page.components';

import {BoxLoading} from '@pb/kit-component-box-loading';
import {Button} from '@pb/kit-component-button';
import {Group} from '@pb/kit-component-group';
import Label from '@pb/kit-component-label';
import StateButton from '@pb/kit-component-state-button';
import {StickyElement} from '@pb/kit-component-sticky-element';
import Typography from '@pb/kit-component-typography';
import {pluralize} from '@pb/kit-formatters';

import {TOOL_TITLE as ASSIGN_TOOL_TITLE} from '../assign/constants';
import {TOOL_TITLE as DISCHARGE_TOOL_TITLE} from '../discharge/constants';

import {ResourceNameHeader} from './components/respource-name.header';
import {
    NO_ASSIGNED_ENTITLEMENT_DESCRIPTION,
    NO_ASSIGNED_ENTITLEMENT_TITLE,
    SELECTION_KEY,
} from './constants';
import {useGetAllEntitlements} from './hooks/useGetAllEntitlements';
import {UpdateUsersEntitlementsPagination} from './pagination';
import {updateUsersEntitlementsStateActions} from './slices/update-users-entitlements-state.slice';
import {updateUsersEntitlementsActions} from './slices/update-users-entitlements.slice';

import {
    updateUsersEntitlementsControlHeader,
    updateUsersEntitlementsControlHeaderSticky,
    updateUsersEntitlementsLoadingContainer,
    updateUsersEntitlementsTools,
} from './update-users-entitlements.module.styl';

import type {ActionWithReason} from 'src/components/save-with-reason/action-with-reason-button';


type Props = {
    mode: 'ASSIGN' | 'DISCHARGE',
    disabled: boolean,
    userIds: number[] | null,
    onCancelBulkAction: () => void,
    onProcessAction: ActionWithReason,
};

export const UpdateUsersEntitlementsView = (props: Props) => {
    const {
        mode,
        disabled,
        userIds,
        onCancelBulkAction,
        onProcessAction,
    } = props;

    const dispatch = useDispatch();
    const {getColumnWidthByColumnId, onStopResize} = useDataTableColumnsWidthOnResize();
    const {loadingError, result, cacheKeys} = useGetAllEntitlements();

    const {
        disabled: isRolesDisabled,
        isExpanded: isRolesExpanded,
        toggleRolesExpansion,
        expandedRoleIds,
        setExpandedRoleIds,
    } = useExpandRoles({records: result?.data || []});

    useEffect(() => {
        return () => {
            dispatch(clearSelection(SELECTION_KEY));
            dispatch(updateUsersEntitlementsActions.clearData());
            dispatch(updateUsersEntitlementsStateActions.clearData());
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const filterResources = useCallback((resourceIds: string[] | null, resourceQuery: string) => {
        dispatch(updateUsersEntitlementsStateActions.setFilters(resourceIds ? {
            resourceIds,
            resourceQuery,
        } : {}));
    }, [dispatch]);

    const noResultMessage = useMemo(() => {
        const filteredResources = cacheKeys.filters.resourceIds as string[] | undefined;
        if (filteredResources && !filteredResources.length) {
            return (
                <NoResultPage
                    title="No resources found for the selected criteria."
                    description=""
                />
            );
        }

        if (result && !result.data.length) {
            return (
                <NoResultPage
                    title={NO_ASSIGNED_ENTITLEMENT_TITLE}
                    description={NO_ASSIGNED_ENTITLEMENT_DESCRIPTION}
                />
            );
        }

        return <div/>;
    }, [cacheKeys.filters, result]);


    return (
        <ServerResponseHandler serverError={loadingError} sectionName="Assign Account Entitlements">
            <ErrorBoundary fallback={<ErrorPage subtitle="Please try again later." title="Something went wrong."/>}>
                <StickyElement className={updateUsersEntitlementsControlHeaderSticky}>
                    <div className={updateUsersEntitlementsControlHeader}>
                        <Group gap="m" justify="space-between" alignItems="center">
                            <Group gap="m" alignItems="center">
                                <div style={{width: 370}}>
                                    <Typography fontWeight="semi-bold" variant="heading-level-3">
                                        <Group gap="m" alignItems="center">
                                            {mode === 'ASSIGN' ? ASSIGN_TOOL_TITLE : DISCHARGE_TOOL_TITLE}
                                        </Group>
                                    </Typography>
                                </div>
                                <ResourceSuggest onSearch={filterResources} id="resource-filter"/>
                                {userIds ? (
                                    <b>
                                        {pluralize(`${userIds.length} user[s] selected`, userIds.length)}
                                    </b>
                                ) : null}
                            </Group>
                            <Group gap="m" alignItems="center">
                                <Label>Expand Roles</Label>
                                <StateButton
                                    {...{
                                        name: 'expand-user-roles',
                                        disabled: isRolesDisabled,
                                        checked: isRolesExpanded,
                                        onChange: toggleRolesExpansion,
                                    }}
                                />
                                <Button variant="Type.SECONDARY" onClick={onCancelBulkAction}>
                                    Cancel
                                </Button>
                                <ActionWithReasonButton
                                    disabled={disabled}
                                    onConfirm={onProcessAction}
                                    action={mode === 'ASSIGN' ? 'Assign' : 'Unassign'}
                                />
                            </Group>
                        </Group>
                    </div>
                </StickyElement>
                <BoxLoading loading={!result}>
                    <div className={updateUsersEntitlementsLoadingContainer}>
                        {!!result?.data.length && (
                            <PreparedEntitlementsView
                                dataTableId="assign-account-entitlements"
                                rows={result.data}
                                selectionKey={SELECTION_KEY}
                                expandedRoleIds={expandedRoleIds}
                                setExpandedRoleIds={setExpandedRoleIds}
                                toolRibbonClassName={updateUsersEntitlementsTools}
                                customOffset={137}
                                getColumnWidthByColumnId={getColumnWidthByColumnId}
                                onStopResize={onStopResize}
                                paginationStripe={<UpdateUsersEntitlementsPagination totalCount={result.totalCount}/>}
                                shortHeader={<ResourceNameHeader/>}
                                resourceFilter={(cacheKeys.filters.resourceQuery as string | undefined) || ''}
                            />
                        )}
                        {noResultMessage}
                    </div>
                </BoxLoading>
            </ErrorBoundary>
        </ServerResponseHandler>
    );
};
